export default function AIGovernancePage() {
  return (
    <div className="min-h-screen bg-white font-playfair">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center justify-center w-full md:w-auto">
              <h1>
                <a href="/">
                  <img 
                    src="https://static.readdy.ai/image/a19705f045df358eda503236ce656b36/846ee1e93f81f94b53dc3e9fa39ac791.png" 
                    alt="VTC Legal - Fractional General Counsel for AI and Tech Companies" 
                    className="h-16 w-auto"
                  />
                </a>
              </h1>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-10">
              <a href="/#services" className="text-gray-900 hover:text-teal-600 font-medium tracking-wide transition-colors">Services</a>
              <a href="/#why-vtc" className="text-gray-900 hover:text-teal-600 font-medium tracking-wide transition-colors">Why VTC</a>
              <a href="/#how-it-works" className="text-gray-900 hover:text-teal-600 font-medium tracking-wide transition-colors">How It Works</a>
              <a href="/#contact" className="bg-black text-white px-8 py-3 rounded-md hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer font-medium">Get Started</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-5xl font-bold text-gray-900 mb-8 font-playfair">
            AI Governance & Compliance
          </h2>
          <p className="text-xl text-gray-700 mb-12 max-w-3xl mx-auto leading-relaxed font-inter">
            Navigate the complex AI regulatory landscape with confidence. Build compliant frameworks that enable innovation while managing risk.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16">
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-8 font-playfair">The Challenge</h3>
              <div className="space-y-6">
                <p className="text-gray-700 leading-relaxed font-inter">
                  AI regulations are evolving rapidly across federal, state, and international jurisdictions. Companies deploying AI face a maze of compliance requirements spanning data privacy, algorithmic accountability, and sector-specific rules.
                </p>
                <p className="text-gray-700 leading-relaxed font-inter">
                  Without proper governance frameworks, AI companies risk regulatory penalties, customer trust issues, and blocked market access. Yet over-compliance can stifle innovation and competitive advantage.
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-8 font-playfair">Our Approach</h3>
              <div className="space-y-6">
                <p className="text-gray-700 leading-relaxed font-inter">
                  We develop practical AI governance frameworks that balance compliance with business objectives. Our approach focuses on risk-based assessments tailored to your specific AI applications and target markets.
                </p>
                <p className="text-gray-700 leading-relaxed font-inter">
                  Rather than generic compliance checklists, we create governance structures that integrate with your development processes and scale with your technology evolution.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-4xl font-bold text-gray-900 mb-16 text-center font-playfair">Comprehensive AI Legal Support</h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-white text-xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-4 font-playfair">Risk Assessment</h4>
              <p className="text-gray-700 font-inter">Comprehensive evaluation of AI systems, data flows, and regulatory exposure across all applicable jurisdictions.</p>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-file-text-line text-white text-xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-4 font-playfair">Policy Development</h4>
              <p className="text-gray-700 font-inter">Custom AI governance policies, data handling procedures, and algorithmic accountability frameworks.</p>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-eye-line text-white text-xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-4 font-playfair">Regulatory Monitoring</h4>
              <p className="text-gray-700 font-inter">Ongoing compliance monitoring and updates as AI regulations evolve across federal and state levels.</p>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-team-line text-white text-xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-4 font-playfair">Team Training</h4>
              <p className="text-gray-700 font-inter">Practical training for development and product teams on AI compliance requirements and best practices.</p>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-global-line text-white text-xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-4 font-playfair">International Compliance</h4>
              <p className="text-gray-700 font-inter">GDPR, EU AI Act, and other international frameworks for companies operating across borders.</p>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-contract-line text-white text-xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-4 font-playfair">Vendor Agreements</h4>
              <p className="text-gray-700 font-inter">AI-specific contract terms for data processors, model providers, and technology partners.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Regulations */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-4xl font-bold text-gray-900 mb-12 text-center font-playfair">Navigate Complex Regulatory Landscape</h3>
          
          <div className="space-y-8">
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
              <h4 className="text-xl font-bold text-gray-900 mb-4 font-playfair">Federal AI Initiatives</h4>
              <p className="text-gray-700 mb-4 font-inter">
                Executive Orders on AI safety, NIST AI Risk Management Framework, and sector-specific guidance from agencies like FTC and SEC.
              </p>
              <ul className="text-gray-600 space-y-2 text-sm font-inter">
                <li>• AI safety testing and evaluation requirements</li>
                <li>• Transparency and disclosure obligations</li>
                <li>• Bias testing and algorithmic accountability</li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
              <h4 className="text-xl font-bold text-gray-900 mb-4 font-playfair">State-Level Regulations</h4>
              <p className="text-gray-700 mb-4 font-inter">
                California Consumer Privacy Act (CCPA), New York's proposed AI regulations, and emerging state-level AI governance requirements.
              </p>
              <ul className="text-gray-600 space-y-2 text-sm font-inter">
                <li>• Automated decision-making disclosures</li>
                <li>• Consumer rights regarding AI systems</li>
                <li>• Employment AI screening requirements</li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
              <h4 className="text-xl font-bold text-gray-900 mb-4 font-playfair">International Standards</h4>
              <p className="text-gray-700 mb-4 font-inter">
                EU AI Act compliance for companies serving European markets, plus emerging frameworks in Canada, UK, and Asia-Pacific regions.
              </p>
              <ul className="text-gray-600 space-y-2 text-sm font-inter">
                <li>• High-risk AI system classifications</li>
                <li>• Conformity assessment requirements</li>
                <li>• Cross-border data transfer compliance</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-4xl font-bold text-white mb-8 font-playfair">
            Ready to Build Compliant AI Systems?
          </h3>
          <p className="text-xl text-gray-300 mb-12 font-inter">
            Get strategic AI governance guidance that enables innovation while managing regulatory risk.
          </p>
          <a href="/#contact" className="bg-teal-600 text-white px-12 py-4 rounded-lg text-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer inline-block font-inter">
            Schedule Consultation
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-50 text-gray-900 py-20 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-600 text-sm font-inter">
            © 2026 VTC Legal. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}